import React, { useState, useMemo, useEffect } from 'react'
import { useTheme } from '../../hooks/useTheme'
import { useDeviceDetection } from '../../hooks/useDeviceDetection'
import { useDisponibilidadEspacio } from '../../hooks/useEspacios'
import { getMotivColor } from '../../types/availability'
import { formatCurrency } from '../../utils/currencyHelpers'
import { parseLocalDateString, formatLocalDateToString } from '../../utils/dateHelpers'
import { toastError } from '../../utils/toastHelper'
import type { DiaDesglose } from '../../types/availability'

interface DateSelectorV2Props {
  espacioId: string
  rangoInicial?: { fecha_entrada: string; fecha_salida: string }
  fechaEntradaInicial?: string
  fechaSalidaInicial?: string
  onDateChange: (selection: {
    fechaEntrada: string | null
    fechaSalida: string | null
    desglose: Array<{ fecha: string; dia_semana?: string; tarifa: number }>
    montoTotal: number
    cantidadUnidades: number
  }) => void
  showPricing?: boolean
  className?: string
}

const DateSelectorV2: React.FC<DateSelectorV2Props> = ({
  espacioId,
  rangoInicial,
  fechaEntradaInicial,
  fechaSalidaInicial,
  onDateChange,
  showPricing = true,
  className = ''
}) => {
  const { colors } = useTheme()
  const { isMobile } = useDeviceDetection()

  // ✅ NO preseleccionar fechas - el usuario debe hacer click para seleccionar
  // Las fechas iniciales solo se usan para consultar disponibilidad y mostrar el mes correcto
  const [fechaEntrada, setFechaEntrada] = useState<string | null>(null)
  const [fechaSalida, setFechaSalida] = useState<string | null>(null)
  const [hoveredDay, setHoveredDay] = useState<string | null>(null)
  const [cantidadUnidades, setCantidadUnidades] = useState<number>(0)

  // State para controlar qué mes se muestra
  const [mesActual, setMesActual] = useState<{ year: number; month: number }>(() => {
    // Si hay fechas iniciales, usar el mes de fecha entrada
    if (fechaEntradaInicial) {
      const fechaInicial = parseLocalDateString(fechaEntradaInicial)
      return { year: fechaInicial.getFullYear(), month: fechaInicial.getMonth() }
    }
    // Si no, usar mes actual
    const hoy = new Date()
    return { year: hoy.getFullYear(), month: hoy.getMonth() }
  })

  // Calcular cuántos meses abarcan las fechas iniciales
  const mesesNecesarios = useMemo(() => {
    if (!fechaEntradaInicial || !fechaSalidaInicial) {
      return 1 // Por defecto 1 mes si no hay fechas
    }

    const fechaInicio = parseLocalDateString(fechaEntradaInicial)
    const fechaFin = parseLocalDateString(fechaSalidaInicial)

    const yearInicio = fechaInicio.getFullYear()
    const monthInicio = fechaInicio.getMonth()
    const yearFin = fechaFin.getFullYear()
    const monthFin = fechaFin.getMonth()

    // Calcular diferencia en meses
    const diffMeses = (yearFin - yearInicio) * 12 + (monthFin - monthInicio) + 1

    return diffMeses
  }, [fechaEntradaInicial, fechaSalidaInicial])

  // Calcular rango de fechas para consultar disponibilidad
  // ✅ SOLO se calcula UNA VEZ con las fechas iniciales
  // ✅ NO cambia cuando el usuario selecciona fechas manualmente
  const rangoConsulta = useMemo(() => {
    if (rangoInicial) {
      return rangoInicial
    }

    if (fechaEntradaInicial && fechaSalidaInicial) {
      return {
        fecha_entrada: fechaEntradaInicial,
        fecha_salida: fechaSalidaInicial
      }
    }

    return null
  }, [rangoInicial, fechaEntradaInicial, fechaSalidaInicial])

  // Fetch disponibilidad detallada
  const { data: detalleEspacio, isLoading } = useDisponibilidadEspacio(
    espacioId,
    rangoConsulta
  )

  // Crear mapa de desglose para acceso rápido
  const desgloseMap = useMemo(() => {
    const map = new Map<string, DiaDesglose>()
    detalleEspacio?.desglose?.forEach(dia => {
      map.set(dia.fecha, dia)
    })
    return map
  }, [detalleEspacio?.desglose])

  // Obtener unidad de reserva
  const unidadReserva = detalleEspacio?.unidad_reserva || 'noche'

  // Generar meses para el calendario (tantos como sean necesarios)
  const mesesCalendario = useMemo(() => {
    const meses: Array<{ year: number; month: number; dias: Date[] }> = []

    // Generar mesesNecesarios meses a partir de mesActual
    for (let i = 0; i < mesesNecesarios; i++) {
      const mes = new Date(mesActual.year, mesActual.month + i, 1)
      const year = mes.getFullYear()
      const month = mes.getMonth()

      const primerDia = new Date(year, month, 1)
      const ultimoDia = new Date(year, month + 1, 0)
      const dias: Date[] = []

      for (let d = new Date(primerDia); d <= ultimoDia; d.setDate(d.getDate() + 1)) {
        dias.push(new Date(d))
      }

      meses.push({ year, month, dias })
    }

    return meses
  }, [mesActual, mesesNecesarios])

  // Calcular límites de navegación basados en fechas iniciales
  const limitesNavegacion = useMemo(() => {
    if (!fechaEntradaInicial || !fechaSalidaInicial) {
      return { minYear: 0, minMonth: 0, maxYear: 9999, maxMonth: 11 }
    }
    const fechaInicio = parseLocalDateString(fechaEntradaInicial)
    const fechaFin = parseLocalDateString(fechaSalidaInicial)

    // Calcular fecha de checkout real
    // Si es "noche", checkout = fecha_salida + 1 día
    // Si es "dia", checkout = fecha_salida
    const fechaCheckout = unidadReserva === 'noche'
      ? new Date(fechaFin.getTime() + 24 * 60 * 60 * 1000)
      : fechaFin

    return {
      minYear: fechaInicio.getFullYear(),
      minMonth: fechaInicio.getMonth(),
      maxYear: fechaCheckout.getFullYear(),
      maxMonth: fechaCheckout.getMonth()
    }
  }, [fechaEntradaInicial, fechaSalidaInicial, unidadReserva])

  // Funciones de navegación de mes (limitadas al rango)
  const irMesAnterior = () => {
    setMesActual(prev => {
      // No permitir ir antes del mes de fecha entrada
      if (prev.year === limitesNavegacion.minYear && prev.month === limitesNavegacion.minMonth) {
        return prev
      }
      const nuevaFecha = new Date(prev.year, prev.month - 1, 1)
      return { year: nuevaFecha.getFullYear(), month: nuevaFecha.getMonth() }
    })
  }

  const irMesSiguiente = () => {
    setMesActual(prev => {
      // No permitir ir después del mes de fecha salida
      if (prev.year === limitesNavegacion.maxYear && prev.month === limitesNavegacion.maxMonth) {
        return prev
      }
      const nuevaFecha = new Date(prev.year, prev.month + 1, 1)
      return { year: nuevaFecha.getFullYear(), month: nuevaFecha.getMonth() }
    })
  }

  // Verificar si se puede navegar
  const puedeIrAnterior = mesActual.year > limitesNavegacion.minYear ||
    (mesActual.year === limitesNavegacion.minYear && mesActual.month > limitesNavegacion.minMonth)

  const puedeIrSiguiente = mesActual.year < limitesNavegacion.maxYear ||
    (mesActual.year === limitesNavegacion.maxYear && mesActual.month < limitesNavegacion.maxMonth)

  // Helpers
  const isDiaDisponible = (fecha: Date): boolean => {
    const fechaStr = formatLocalDateToString(fecha)
    const dia = desgloseMap.get(fechaStr)
    return dia ? dia.disponible_dia : false
  }

  const isDiaEnRango = (fecha: Date): boolean => {
    if (!fechaEntrada || !fechaSalida) return false
    const fechaStr = formatLocalDateToString(fecha)
    return fechaStr >= fechaEntrada && fechaStr <= fechaSalida
  }

  const getMotivoInfo = (fecha: Date): {
    motivo: string | null | undefined
    color: string
    tarifas: { simple?: number; multiple?: number | null; nombre_simple?: string; nombre_multiple?: string | null } | null
  } => {
    const fechaStr = formatLocalDateToString(fecha)
    const dia = desgloseMap.get(fechaStr)

    if (!dia) {
      return { motivo: null, color: '#9ca3af', tarifas: null }
    }

    return {
      motivo: dia.motivo_indisponibilidad,
      color: getMotivColor(dia.motivo_indisponibilidad),
      tarifas: {
        simple: dia.tarifa_simple,
        multiple: dia.tarifa_multiple,
        nombre_simple: dia.nombre_tarifa_simple,
        nombre_multiple: dia.nombre_tarifa_multiple
      }
    }
  }

  // Validar que el rango sea continuo (sin días no disponibles)
  // ✅ Para "noche": no validar el último día (es checkout, no noche de estancia)
  // ✅ Para "día": validar hasta el último día (inclusive)
  const validarRangoContinuo = (inicio: string, fin: string): boolean => {
    const fechaInicio = parseLocalDateString(inicio)
    const fechaFin = parseLocalDateString(fin)

    const esNoche = unidadReserva === 'noche'
    const fechaValidacionFin = esNoche
      ? new Date(fechaFin.getFullYear(), fechaFin.getMonth(), fechaFin.getDate() - 1)
      : fechaFin

    let fecha = new Date(fechaInicio)
    while (fecha <= fechaValidacionFin) {
      const fechaStr = formatLocalDateToString(fecha)
      const dia = desgloseMap.get(fechaStr)
      if (!dia || !dia.disponible_dia) {
        return false
      }
      fecha.setDate(fecha.getDate() + 1)
    }

    return true
  }

  // Click handler para días
  const handleClickDia = (fecha: Date) => {
    const fechaStr = formatLocalDateToString(fecha)
    const disponible = isDiaDisponible(fecha)

    // Validar que la fecha no sea anterior a hoy
    const hoy = new Date()
    hoy.setHours(0, 0, 0, 0)
    const fechaSeleccionada = new Date(fecha)
    fechaSeleccionada.setHours(0, 0, 0, 0)

    if (fechaSeleccionada < hoy) {
      toastError('No puedes seleccionar fechas anteriores a hoy')
      return
    }

    // Si no hay fecha de entrada, seleccionarla (debe estar disponible)
    if (!fechaEntrada) {
      if (!disponible) return
      setFechaEntrada(fechaStr)
      setFechaSalida(null)
      return
    }

    // Si hay entrada pero no salida
    if (fechaEntrada && !fechaSalida) {
      // ✅ Para "noche": fecha_salida NO necesita estar disponible (es checkout)
      // ✅ Para "día": fecha_salida DEBE estar disponible
      const esNoche = unidadReserva === 'noche'
      if (!esNoche && !disponible) {
        // Para "día", la fecha de salida debe estar disponible
        return
      }

      // Si clic en fecha anterior, resetear
      if (fechaStr < fechaEntrada) {
        if (!disponible) return
        setFechaEntrada(fechaStr)
        setFechaSalida(null)
        return
      }

      // Validación: para 'noche' no permitir mismo día
      if (esNoche && fechaStr === fechaEntrada) {
        toastError('Para reservas por noche, debes seleccionar al menos 1 noche (fecha de salida debe ser posterior)')
        return
      }

      // Validar que el rango sea continuo
      if (!validarRangoContinuo(fechaEntrada, fechaStr)) {
        toastError('El rango seleccionado contiene días no disponibles. Selecciona un rango continuo.')
        setFechaEntrada(null)
        setFechaSalida(null)
        return
      }

      setFechaSalida(fechaStr)
      return
    }

    // Si ya hay ambas, resetear
    if (!disponible) return
    setFechaEntrada(fechaStr)
    setFechaSalida(null)
  }

  // Calcular desglose y total cuando cambian las fechas
  useEffect(() => {
    if (!fechaEntrada || !fechaSalida || !detalleEspacio) {
      setCantidadUnidades(0)
      onDateChange({
        fechaEntrada: fechaEntrada,
        fechaSalida: fechaSalida,
        desglose: [],
        montoTotal: 0,
        cantidadUnidades: 0
      })
      return
    }

    // Construir desglose para el rango seleccionado
    const fechaInicio = parseLocalDateString(fechaEntrada)
    const fechaFin = parseLocalDateString(fechaSalida)

    // Calcular diferencia de días
    const diffTime = fechaFin.getTime() - fechaInicio.getTime()
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

    // Cantidad de unidades según unidad_reserva
    const cantidadUnidadesCalculada = unidadReserva === 'noche' ? diffDays : diffDays + 1
    setCantidadUnidades(cantidadUnidadesCalculada)

    const desgloseRango: DiaDesglose[] = []
    let total = 0

    // Para 'noche': iterar hasta (fecha_salida - 1)
    // Para 'dia': iterar hasta fecha_salida (inclusive)
    const fechaIteracionFin = unidadReserva === 'noche'
      ? new Date(fechaFin.getFullYear(), fechaFin.getMonth(), fechaFin.getDate() - 1)
      : fechaFin

    // Determinar si usar tarifa múltiple (igual que DateAvailabilityModal líneas 362)
    const usarMultiple = cantidadUnidadesCalculada >= 2

    let fecha = new Date(fechaInicio)
    while (fecha <= fechaIteracionFin) {
      const fechaStr = formatLocalDateToString(fecha)
      const dia = desgloseMap.get(fechaStr)

      if (dia) {
        // Si cantidadUnidades >= 2: TODOS los días usan tarifa_multiple (si existe)
        // Si cantidadUnidades < 2: TODOS los días usan tarifa_simple
        const tarifa = usarMultiple
          ? (dia.tarifa_multiple ?? dia.tarifa_simple)
          : dia.tarifa_simple
        total += tarifa

        // Mapear desglose igual que DateAvailabilityModal (líneas 374-379)
        desgloseRango.push({
          fecha: dia.fecha,
          dia_semana: dia.dia_semana,
          tarifa: tarifa
        })
      }

      fecha.setDate(fecha.getDate() + 1)
    }

    onDateChange({
      fechaEntrada,
      fechaSalida,
      desglose: desgloseRango,
      montoTotal: total,
      cantidadUnidades: cantidadUnidadesCalculada
    })
  }, [fechaEntrada, fechaSalida, detalleEspacio, desgloseMap, unidadReserva, onDateChange])

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-t-transparent rounded-full"
          style={{ borderColor: `${colors.primary}40`, borderTopColor: 'transparent' }}
        />
        <span className="ml-3 text-gray-600">Cargando disponibilidad...</span>
      </div>
    )
  }

  return (
    <div className={`date-selector-v2 ${className}`}>
      {/* Leyenda */}
      <div className="flex flex-wrap gap-2 sm:gap-3 md:gap-4 mb-6 text-xs sm:text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#22c55e' }} />
          <span>Disponible</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#ef4444' }} />
          <span>Reservado</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f97316' }} />
          <span>Bloqueo temporal</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded" style={{ backgroundColor: '#9ca3af' }} />
          <span>No disponible</span>
        </div>
      </div>

      {/* Selector de mes con navegación */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        {/* Header del mes con navegación */}
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={irMesAnterior}
            disabled={!puedeIrAnterior}
            className={`p-2 rounded-lg transition-colors ${
              puedeIrAnterior
                ? 'hover:bg-gray-100 cursor-pointer'
                : 'opacity-30 cursor-not-allowed'
            }`}
            aria-label="Mes anterior"
          >
            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <h3 className="text-lg font-bold text-gray-900">
            {new Date(mesActual.year, mesActual.month).toLocaleDateString('es-CL', { month: 'long', year: 'numeric' })}
          </h3>

          <button
            onClick={irMesSiguiente}
            disabled={!puedeIrSiguiente}
            className={`p-2 rounded-lg transition-colors ${
              puedeIrSiguiente
                ? 'hover:bg-gray-100 cursor-pointer'
                : 'opacity-30 cursor-not-allowed'
            }`}
            aria-label="Mes siguiente"
          >
            <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        {/* Grid de meses (dinámico según rango de fechas) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {mesesCalendario.map(({ year, month, dias }) => (
            <div key={`${year}-${month}`}>
              <h4 className="text-center text-md font-semibold text-gray-700 mb-3">
                {new Date(year, month).toLocaleDateString('es-CL', { month: 'long', year: 'numeric' })}
              </h4>

              {/* Días de la semana */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((dia) => (
                  <div key={dia} className="text-center text-xs font-semibold text-gray-600 py-1">
                    {dia}
                  </div>
                ))}
              </div>

              {/* Días del mes */}
              <div className="grid grid-cols-7 gap-1">
                {/* Espacios vacíos para alinear primer día */}
                {Array.from({ length: dias[0]?.getDay() || 0 }).map((_, i) => (
                  <div key={`empty-${i}`} />
                ))}

                {/* Días */}
                {dias.map((fecha) => {
                  const fechaStr = formatLocalDateToString(fecha)
                  const disponible = isDiaDisponible(fecha)
                  const enRango = isDiaEnRango(fecha)
                  const { color, tarifas } = getMotivoInfo(fecha)
                  const isHovered = hoveredDay === fechaStr

                  // Verificar si la fecha es anterior a hoy
                  const hoy = new Date()
                  hoy.setHours(0, 0, 0, 0)
                  const fechaSeleccionada = new Date(fecha)
                  fechaSeleccionada.setHours(0, 0, 0, 0)
                  const esPasado = fechaSeleccionada < hoy

                  // ✅ Para noche: fecha_salida puede estar ocupada (es checkout)
                  // Para día: todos los días deben estar disponibles
                  const esNoche = unidadReserva === 'noche'
                  const puedeSerFechaSalida = esNoche && fechaEntrada && !fechaSalida &&
                    parseLocalDateString(fechaStr) > parseLocalDateString(fechaEntrada)
                  const botonDeshabilitado = esPasado || (!disponible && !puedeSerFechaSalida)

                  return (
                    <div key={fechaStr} className="relative">
                      <button
                        onClick={() => handleClickDia(fecha)}
                        onMouseEnter={() => setHoveredDay(fechaStr)}
                        onMouseLeave={() => setHoveredDay(null)}
                        disabled={botonDeshabilitado}
                        className={`
                          w-full aspect-square rounded-lg text-sm font-medium transition-all flex flex-col items-center justify-center p-1
                          ${esPasado ? 'cursor-not-allowed opacity-30' : ''}
                          ${!esPasado && (disponible || puedeSerFechaSalida) ? 'cursor-pointer hover:scale-110' : ''}
                          ${!esPasado && !disponible && !puedeSerFechaSalida ? 'cursor-not-allowed opacity-50' : ''}
                          ${enRango ? 'ring-2 ring-offset-1' : ''}
                        `}
                      style={{
                        backgroundColor: esPasado
                          ? '#d1d5db'
                          : disponible
                            ? color
                            : puedeSerFechaSalida
                              ? '#dbeafe'  // Azul claro para días válidos como checkout
                              : '#9ca3af',  // Gris oscuro para días completamente ocupados
                        color: esPasado ? '#9ca3af' : (puedeSerFechaSalida && !disponible ? '#1e40af' : '#ffffff'),
                        ringColor: enRango ? colors.primary : 'transparent',
                        border: puedeSerFechaSalida && !disponible ? '2px solid #3b82f6' : 'none'
                      }}
                    >
                      <span className={`text-sm sm:text-base font-bold ${esPasado ? 'line-through' : ''}`}>{fecha.getDate()}</span>
                      {showPricing && !esPasado && disponible && tarifas?.simple && (
                        <span className="text-[10px] sm:text-[9px] leading-none mt-0.5 opacity-90 font-semibold">
                          {formatCurrency(
                            cantidadUnidades >= 2
                              ? (tarifas.multiple ?? tarifas.simple)
                              : tarifas.simple
                          )}
                        </span>
                      )}
                    </button>

                    {/* Tooltip con precio (solo desktop con hover) */}
                    {showPricing && !esPasado && tarifas && disponible && !isMobile && isHovered && (
                      <div
                        className="absolute z-10 bg-gray-900 text-white text-xs rounded-lg p-2 shadow-lg bottom-full mb-1 left-1/2 transform -translate-x-1/2 whitespace-nowrap pointer-events-none"
                      >
                        <div className="font-semibold">{tarifas.nombre_simple || 'Tarifa'}</div>
                        <div>{formatCurrency(tarifas.simple || 0)}/{unidadReserva}</div>
                        {tarifas.multiple && (
                          <div className="text-xs text-gray-300 mt-1">
                            {tarifas.nombre_multiple || 'Múltiple'}: {formatCurrency(tarifas.multiple)}
                          </div>
                        )}
                      </div>
                    )}
                    </div>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Leyenda de colores (solo para unidad_reserva='noche') */}
      {unidadReserva === 'noche' && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-xs sm:text-sm">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: colors.primary }}></div>
            <span className="text-gray-700">Disponible</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded border-2" style={{ backgroundColor: '#dbeafe', borderColor: '#3b82f6' }}></div>
            <span className="text-gray-700">Válido para checkout</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: '#9ca3af' }}></div>
            <span className="text-gray-700">Ocupado</span>
          </div>
        </div>
      )}

      {/* Resumen de selección */}
      {fechaEntrada && fechaSalida && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-blue-900">Rango seleccionado</p>
              <p className="text-blue-800">
                {parseLocalDateString(fechaEntrada).toLocaleDateString('es-CL')} - {parseLocalDateString(fechaSalida).toLocaleDateString('es-CL')}
              </p>
            </div>
            <button
              onClick={() => {
                setFechaEntrada(null)
                setFechaSalida(null)
              }}
              className="text-sm text-blue-700 hover:text-blue-900 font-medium"
            >
              Limpiar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default DateSelectorV2
